import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Button, Card, IconButton, Stack, Typography } from "@mui/material";
import React from "react";
import { useDispatch } from "react-redux";
import { fileService } from "../../../Core/Api/services";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { ItemField, UpdateItemRootOnBasket } from "../../../Redux/Reducers/redux-slice-basket";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormSection from "../../Forms/FormSection/form-section";
import { DropFileZoneContext } from "../../Misc/DropFileZone/drop-file-zone";
import { ModalContext } from "../../Modals";
import ModalAddAttachement, {
  ModalAddAttachementElementProps,
} from "../../Modals/ModalAddAttachement/modal-add-attachment";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import ConfigFormIcon from "../ConfigFormIcon";
import { useTranslation } from "react-i18next";
interface ConfigCommonFormDeliveryProps {
  item: DbOrderBasketItemInfos;
}

interface AttachmentDto {
  name: string;
  fileIndex: number;
}

const ConfigCommonSectionFormAttachments: React.FC<ConfigCommonFormDeliveryProps> = (props) => {
  const { t } = useTranslation();
  const { item } = props;
  const dispatch = useDispatch();

  const enqueueSnackbar = useCustomSnackbar();

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [attachmentFiles, setAttachmentFiles] = React.useState<AttachmentDto[]>([]);

  const handleAddAttachment = () => {
    if (!openModal) return;

    const onClose: ModalAddAttachementElementProps["onClose"] = (data) => {
      if (data.success) {
        setAttachmentFiles((prevState) => [
          ...prevState,
          {
            name: data.files[0].name,
            itemId: item.id,
            fileIndex: attachmentFiles.length,
          },
        ]);
        // Set transferId
        dispatch(
          UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.DeliveryAttachment, value: data.resourceId })
        );
      }
    };

    openModal<ModalAddAttachementElementProps>(ModalAddAttachement, {
      onClose,
      resourceId: item.id,
      context: DropFileZoneContext.OrderBasketDelivery,
    });
  };

  const handleDeleteAttachment = (fileIndex) => {
    const attachment = attachmentFiles.find((file) => file.fileIndex === fileIndex);
    if (!attachment) return;

    fileService.DeleteDeliveryFileFromItem(item.id, attachment.fileIndex).then((response) => {
      if (!response.success) enqueueSnackbar(t("alert:error.deletingFile", { message: response.message }), "error");
      setAttachmentFiles((prevState) => prevState.filter((file) => file.fileIndex !== fileIndex));
    });
  };

  const getItemAttachedFiles = () => {
    if (!item.id) return;

    fileService.GetOrderDeliveryAttachedFileInfos(item.id).then((response) => {
      if (response.success && response.file) {
        setAttachmentFiles(
          response.file?.files?.map((file, index) => ({
            fileIndex: index,
            itemId: item.id,
            name: file.name!,
          })) || []
        );
      }
    });
  };

  React.useEffect(() => {
    getItemAttachedFiles();
  }, [item.id, item.deliveryFileTransferId]);

  return (
    <>
      <FormSection
        icon={<ConfigFormIcon icon="attachment" />}
        title={t("common:attachments")}
        status={
          !attachmentFiles.length
            ? item.selectedProduct?.isDeliveryAttachmentMandatory
              ? "warning"
              : "todo"
            : "complete"
        }
        statusText={
          !attachmentFiles.length
            ? item.selectedProduct?.isDeliveryAttachmentMandatory
              ? t("common:configurationRequired")
              : t("form:statusText.configurationIsOptional")
            : t("common:filesAttached", { count: attachmentFiles.length })
        }
      >
        <Button onClick={handleAddAttachment}>{t("button:addAttachment")}</Button>
        <Stack spacing={1} mt={1}>
          {attachmentFiles.map((attachment, index) => (
            <AttachmentCard key={index} {...attachment} onDelete={handleDeleteAttachment} />
          ))}
        </Stack>
      </FormSection>
    </>
  );
};

export default ConfigCommonSectionFormAttachments;

const AttachmentCard = (props: AttachmentDto & { onDelete: (fileIndex: number) => void }) => {
  const { t } = useTranslation();
  const { name, fileIndex, onDelete } = props;

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleDelete = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      onConfirm: () => onDelete(fileIndex),
      title: t("modal:title.confirmAttachmentDeletion"),
    });
  };

  return (
    <Card variant="outlined" sx={{ p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">{name}</Typography>
        <IconButton onClick={handleDelete}>
          <DeleteForeverIcon color="error" />
        </IconButton>
      </Stack>
    </Card>
  );
};
